import type React from 'react';

import { Card, CardBody, CardHeader } from '@change/ds-core-ui/Card';
import { Image } from '@change/ds-core-ui/Image';
import { Text } from '@change/ds-core-ui/Text';

import type { Article } from './hooks';
import { useArticleList } from './hooks';

/**
 * Requests a downsized, modern-format rendition via Contentful's image API rather than the
 * full-resolution original — https://www.contentful.com/developers/docs/references/images-api/
 */
function getOptimizedImageUrl(url: string): string {
	const optimized = new URL(url);
	optimized.searchParams.set('w', '600');
	optimized.searchParams.set('fm', 'webp');
	optimized.searchParams.set('q', '75');
	return optimized.toString();
}

const ArticleCard: React.FC<Article & { eager: boolean; onClick: () => void }> = ({
	heading,
	slug,
	heroImage,
	eager,
	onClick,
}) => (
	<Card
		as="a"
		elevation
		data-qa={`${slug}-link`}
		href={`/petition-guides/${slug}`}
		onClick={onClick}
		className="h-full content-start no-underline"
	>
		{heroImage && (
			<CardHeader variant="media">
				<Image
					src={getOptimizedImageUrl(heroImage.url)}
					alt={heroImage.description || heroImage.title}
					loading={eager ? 'eager' : 'lazy'}
					className="w-full object-cover"
				/>
			</CardHeader>
		)}
		<CardBody>
			<Text as="h2" variant="heading" size="x-small">
				{heading}
			</Text>
		</CardBody>
	</Card>
);

/**
 * Renders every article as a real crawlable `<a>`. The list must not be capped here
 * either — see {@link useArticleList}. The grid container itself is the focus target moved
 * to on a pagination change (also see {@link useArticleList}), so it carries `tabIndex={-1}`
 * and an accessible name. It is rendered as a `<section>` rather than a `<div>` because ARIA 1.2
 * prohibits `aria-label`/`aria-labelledby` on the `<div>`'s implicit `role="generic"` — a `<section>`
 * with an accessible name maps to `role="region"` instead, so the name is actually exposed to
 * assistive tech.
 */
export const ArticleList: React.FC<EmptyProps> = () => {
	const {
		data: { articles, containerRef, containerAriaLabel },
		actions: { trackArticleClick },
	} = useArticleList();

	return (
		<section
			ref={containerRef}
			tabIndex={-1}
			aria-label={containerAriaLabel}
			className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
		>
			{articles.map((article, index) => (
				<ArticleCard
					key={article.slug}
					{...article}
					eager={index === 0}
					onClick={() => trackArticleClick(article.slug)}
				/>
			))}
		</section>
	);
};
